import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Old tab URLs and the unhealthy-Route landing. Each one replaces the URL
// instead of rendering a tab that no longer exists.
export const useRouteSubpageRedirects = ({
  routeId,
  subpage,
  effectiveSubpage,
  providerId,
  pageLoaded,
  routeStatus,
}: {
  routeId: string | undefined;
  // The :subpage param as it is in the URL (absent on the bare URL).
  subpage: string | undefined;
  effectiveSubpage: string;
  providerId: string | undefined;
  pageLoaded: boolean;
  routeStatus: string | undefined;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // The provider and webhook tabs became sections of the flow map's provider
  // panel. Their URLs still exist in bookmarks, so send them to the equivalent
  // selection instead of rendering a tab that no longer exists.
  useEffect(() => {
    if (!routeId || !providerId) return;
    if (effectiveSubpage !== 'provider' && effectiveSubpage !== 'webhook')
      return;
    navigate(`/routes/${routeId}`, {
      replace: true,
      state: {
        flowSelection: {
          kind: 'account',
          integrationId: providerId,
          section: effectiveSubpage === 'webhook' ? 'webhook' : undefined,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, providerId, effectiveSubpage]);

  // Same URLs, but the provider never arrived: land on the flow map rather than
  // rendering a tab that no longer exists.
  useEffect(() => {
    if (!pageLoaded || !routeId || providerId) return;
    if (effectiveSubpage !== 'provider' && effectiveSubpage !== 'webhook')
      return;
    navigate(`/routes/${routeId}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, providerId, effectiveSubpage]);

  // Arriving at the bare /routes/:id of an unhealthy Route opens Logs, because
  // that is where the reason lives. ONCE, on arrival — a standing rule would
  // make Configurations unreachable, since its tab sends the user back to this
  // same bare URL. After this fires the user drives, and the bare URL means
  // Configurations like it does everywhere else. The ref is per mount, and
  // RouteDetailsRoute keys the screen on :route_id, so a different Route gets
  // a fresh one.
  const unhealthyLandingSettled = useRef(false);
  useEffect(() => {
    if (unhealthyLandingSettled.current) return;
    // Wait for the real status: the placeholder Route reads healthy, and
    // latching on it would skip the redirect for every unhealthy Route.
    if (!pageLoaded || !routeId) return;
    unhealthyLandingSettled.current = true;
    // A caller that asked for a panel (or for the add-destination highlight)
    // has already chosen Configurations as surely as a named tab would have.
    // Landing on Logs would drop that request along with the state carrying
    // it — and an unhealthy Route is exactly the one a user is most likely
    // to be attaching a destination to.
    const askedForConfigurations =
      !!(location.state as any)?.flowSelection ||
      !!(location.state as any)?.highlightAddDestination;
    if (subpage || askedForConfigurations || routeStatus !== 'unhealthy')
      return;
    navigate(`/routes/${routeId}/logs`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLoaded, routeStatus, subpage, routeId]);

  // The destinations and General (overview) tabs are gone too — the flow map
  // page carries both. Their URLs stay valid for bookmarks and land there.
  useEffect(() => {
    if (
      !routeId ||
      (effectiveSubpage !== 'destinations' && effectiveSubpage !== 'overview')
    )
      return;
    navigate(`/routes/${routeId}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, effectiveSubpage]);
};
